/**
 * Core requisition lifecycle state, as the API serialises it.
 *
 * DRAFT is raised in TeamForm; APPROVED means guardrails cleared with no
 * external id yet; OPEN carries an ATS id; CLOSED is final. A source
 * system's own label rides separately on externalStatus and drives nothing.
 */
export const REQUISITION_STATES = [
  "DRAFT",
  "APPROVED",
  "OPEN",
  "CLOSED",
] as const;

export type RequisitionState = (typeof REQUISITION_STATES)[number];

/** Still claiming capacity on the position. */
export const ACTIVE_REQUISITION_STATES: readonly RequisitionState[] = [
  "DRAFT",
  "APPROVED",
  "OPEN",
];

const LEGACY_FILLED_STATE = "FILLED";

export const isRequisitionState = (
  value: string | null | undefined
): value is RequisitionState =>
  Boolean(value) &&
  (REQUISITION_STATES as readonly string[]).includes(value as string);

export const isActiveRequisitionState = (
  value: string | null | undefined
): boolean =>
  Boolean(value) &&
  (ACTIVE_REQUISITION_STATES as readonly string[]).includes(value as string);

/** No ATS externalId yet — do not surface the generated REQ- id (MS-1062). */
export const isRequisitionStateWithoutAtsId = (
  value: string | null | undefined
): boolean => value === "DRAFT" || value === "APPROVED";

/**
 * Label for display. GraphQL enum values are SHOUTED; a source system's own
 * status is not, and is shown exactly as it was given to us.
 */
export const formatRequisitionState = (
  value: string | null | undefined
): string => {
  const trimmed = value?.trim();
  if (!trimmed) {
    return "";
  }
  if (trimmed === LEGACY_FILLED_STATE) {
    return "Filled";
  }
  if (!isRequisitionState(trimmed)) {
    return trimmed;
  }
  return trimmed.charAt(0) + trimmed.slice(1).toLowerCase();
};
